import { useState, useEffect } from 'react';
import type { ArtReproduction } from '../types';

const MOBILE_BREAKPOINT = 480;   // Use mobile thumbnail (200px)
const TABLET_BREAKPOINT = 1024;  // Use regular thumbnail (400px); above = full image

export function useViewportWidth(): number {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : TABLET_BREAKPOINT
  );
  useEffect(() => {
    const onResize = (): void => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

/**
 * Returns the best image URL for the current viewport for artwork display.
 * Mobile: smallest (mobileThumbnailUrl) for fast load.
 * Tablet: thumbnail.
 * Desktop: full image.
 */
export function getArtworkDisplayUrl(
  artwork: { imageUrl: string; thumbnailUrl?: string; mobileThumbnailUrl?: string },
  width: number
): string {
  if (width < MOBILE_BREAKPOINT && artwork.mobileThumbnailUrl) {
    return artwork.mobileThumbnailUrl;
  }
  if (width < TABLET_BREAKPOINT && artwork.thumbnailUrl) {
    return artwork.thumbnailUrl;
  }
  return artwork.imageUrl;
}

/**
 * Hook: viewport-aware display URL for artwork. Updates on resize.
 * Use for grid/card display so mobile gets the small image.
 */
export function useArtworkDisplayUrl(artwork: ArtReproduction | null): string {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : TABLET_BREAKPOINT
  );

  useEffect(() => {
    const onResize = (): void => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!artwork) return '';
  return getArtworkDisplayUrl(artwork, width);
}
